import {
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridOptions,
  ModelUpdatedEvent,
  Module,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  getFilterOnCellContextMenu,
  heliosClientSideRowConfig,
  HeliosColumnTypes,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
} from '@helios/ag-grid-config'
import { formatDates } from '@helios/common-api'
import {
  RootState,
  SavedView,
  selectActiveSessionReconDate,
} from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { prettyFormatNumber } from '@helios/utils'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState, TableLayoutComponent } from '@northfork/ag-grid-angular'
import { composeFieldPath, formatDate, ISODateFormat } from '@northfork/basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { DistributionsAccruedInterestGridKey } from '../../../panel-key'
import {
  AccruedInterest,
  InterestFormat,
} from '../../models/accrued-interest.model'

const accruedInterestPath = composeFieldPath([] as AccruedInterest[])

/**
 * Number formatter to 6 decimal places
 */
export function columnValueFormatter(value: unknown) {
  if (value === 0) {
    // Return itself just in case it's a zero
    return '0'
  }

  if (!value) {
    return ''
  }

  if (!isNaN(Number(value))) {
    return prettyFormatNumber(Number(value), 6)
  }

  return value
}

@Component({
  selector: 'hs-accrued-interest-grid[data][interestFormat]',
  templateUrl: './accrued-interest-grid.component.html',
})
export class AccruedInterestGridComponent implements OnInit, OnDestroy {
  private _interestColType: NfColumnTypes =
    HeliosColumnTypes.PreciseNumber as unknown as NfColumnTypes

  @ViewChild('table')
  readonly table?: TableLayoutComponent<AccruedInterest>

  private readonly destroySubject$ = new Subject<void>()

  @Input() data: AccruedInterest[] | null = null
  clearFiltersOnAccountLoadToggle = new FormControl(true)

  @Input() set interestFormat(format: InterestFormat | null) {
    if (format == null) {
      return
    }
    switch (format) {
      case InterestFormat.Decimal:
        this._interestColType =
          HeliosColumnTypes.PreciseNumber as unknown as NfColumnTypes
        break
      case InterestFormat.Dollar:
        this._interestColType = NfColumnTypes.Number
        break
    }
  }
  private _filterState: { [columnName: string]: unknown } | undefined
  @Input() set savedViews(savedViewsStates: SavedView[] | null | undefined) {
    this._filterState = savedViewsStates?.find(
      view => view.gridKey === DistributionsAccruedInterestGridKey,
    )?.savedView.filterState
  }

  readonly gridModules: Module[] = [FiltersToolPanelModule]
  gridApi?: AgGridApi
  gridOptions: GridOptions = this.createGridOptions()
  sessionReconDate?: Date
  hideFutureAccrualsToggle = new FormControl(false)
  clearFiltersEnabled = false

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly store: Store<RootState>,
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.hideFutureAccrualsToggle.valueChanges.subscribe(value => {
      this.setHideFutureAccrualsFilter(value)
    })
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }

  getInterestColType(): NfColumnTypes {
    return this._interestColType
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  //#region GridConfig
  private createGridOptions(): GridOptions {
    return {
      columnDefs: this.createBaseColumnDefs(),
      ...heliosClientSideRowConfig,
      onRowDataChanged: event => this.onRowDataChanged(event),
      ...disableChangeDetectionOnScroll(this.changeDetector),
      onFilterChanged: event => this.filterChanged(event),
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
      ]),
      onGridReady: async params => {
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
        this.gridApi = params

        /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
        const agGridStateSaved = await this.savedViewService.manageSavedView(
          params,
          DistributionsAccruedInterestGridKey,
          this.destroySubject$,
          true,
          this.getCurrentPanelSettingsObject(),
        )

        this.applyGridConfig(agGridStateSaved)
      },
      onModelUpdated: event =>
        event.newData && this._getAndApplyColumnFilter(event),
      getContextMenuItems: params => getFilterOnCellContextMenu(params),
    }
  }

  private readonly filterChanged = (event: FilterChangedEvent) => {
    this.clearFiltersEnabled = event.api.isAnyFilterPresent() ?? false
    this.onHideFutureAccrualDateFilterChanged(event)
  }

  private _getAndApplyColumnFilter(
    event: ModelUpdatedEvent | FirstDataRenderedEvent,
  ): void {
    if (!event.api.isColumnFilterPresent()) {
      if (this._filterState) {
        // Apply preserved filters once grid is rendered
        event.api.setFilterModel(this._filterState)
        setTimeout(() => {
          event.api.onFilterChanged()
        })
      }
    }
  }

  // @internal
  createBaseColumnDefs(): ColDef[] {
    return [
      {
        field: accruedInterestPath('date'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
        sort: 'desc',
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },
      {
        field: accruedInterestPath('databaseBODAccrual'),
        valueGetter: ({ data }) =>
          columnValueFormatter(data.databaseBODAccrual),
        headerName: 'Actual BoD',
      },
      {
        field: accruedInterestPath('databaseEODAccrual'),
        valueGetter: ({ data }) =>
          columnValueFormatter(data.databaseEODAccrual),
        headerName: 'Actual EoD',
      },
      {
        field: accruedInterestPath('calculatedBODAccrual'),
        valueGetter: ({ data }) =>
          columnValueFormatter(data.calculatedBODAccrual),
        headerName: 'Calc BoD',
      },
      {
        field: accruedInterestPath('llaBODAccrual'),
        valueGetter: ({ data }) => columnValueFormatter(data.llaBODAccrual),
        headerName: 'LLA BoD',
      },
      {
        field: accruedInterestPath('llaEODAccrual'),
        valueGetter: ({ data }) => columnValueFormatter(data.llaEODAccrual),
        headerName: 'LLA EoD',
      },
    ]
  }

  async setHideFutureAccrualsFilter(value: boolean) {
    const entryDateFilterComponent = this.gridApi?.api.getFilterInstance('date')
    if (value && this.sessionReconDate) {
      entryDateFilterComponent?.setModel({
        type: 'lessThanOrEqual',
        filterType: 'date',
        filter: formatDate(this.sessionReconDate, ISODateFormat),
      })
    } else entryDateFilterComponent?.setModel(null)
    //applied timeout so that the onFilterChanged is called once the filter is correctly applied on the column
    await new Promise(f => setTimeout(f, 40))
    this.gridApi?.api.onFilterChanged()
  }

  onHideFutureAccrualDateFilterChanged(event: FilterChangedEvent) {
    const filterModel = event.api.getFilterInstance('date')?.getModel()
    const todayDateStr = formatDate(this.sessionReconDate, ISODateFormat)
    const isAccrualsNotFutureFilter =
      filterModel &&
      filterModel.type === 'lessThanOrEqual' &&
      filterModel.filter === todayDateStr
    this.hideFutureAccrualsToggle.setValue(isAccrualsNotFutureFilter, {
      emitEvent: false,
    })
  }

  clearFilters() {
    this.gridApi?.api.setFilterModel(null)
  }

  private onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data == null) {
      event.api.showLoadingOverlay()
      return
    }
  }
}
